"use client";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/atoms/tabs";
import { IAreas, IImages, IPAGEFormacoe, IPAGEFormacoesComponent } from "@/services/types";
import { useEffect, useState } from "react";
import RenderHtmlContentDescritpions from "../common/RenderHtmlContentDescritpions";
import { ItemDescSection } from "../common/ItemDescSection";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/atoms/carousel";
import Image from "next/image";
import { getHighlightPositions } from "@/lib/utils";

interface SectionProps {
    formacoes: IPAGEFormacoe;
}


interface FormacoesProps {
    formacoes: IPAGEFormacoesComponent[];
}

interface CursosProps {
    cursos_session: IAreas;
}

// Componente helper para renderizar texto com palavras destacadas
const HighlightText = ({ text, highlightWords, className, highlightClassName }: {
    text: string;
    highlightWords: string;
    className?: string;
    highlightClassName?: string;
}) => {
    if (!text) return null;

    const positions = getHighlightPositions(text, highlightWords);
    const words = text.split(" ");

    return (
        <span className={className}>
            {words.map((word, i) => {
                const isHighlighted = positions.includes(i);
                return (
                    <span
                        key={i}
                        className={isHighlighted ? highlightClassName || "font-bold" : "font-medium"}
                        style={{ marginRight: "0.25rem" }}
                    >
                        {word}
                    </span>
                );
            })}
        </span>
    );
};

const CursoCard = ({ image, title }: IImages) => {
    const imageUrl = image?.url || '/placeholder-curso.png';

    return (
        <div className="w-full h-82 flex flex-col bg-white rounded-3xl  my-4 shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105 hover:shadow-xl">
            {/* Main image container */}
            <div className="relative w-full h-62 md:h-56 lg:h-62 bg-zinc-800 rounded-3xl overflow-hidden">
                {/* Background image */}
                <Image
                    className="object-cover"
                    fill
                    src={imageUrl}
                    alt={image?.alternativeText || title || "curso"}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                />

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
            </div>

            {/* Content below image */}
            <div className="flex-1 px-4 md:px-5 py-3 md:py-4 flex items-center justify-center ">
                <h3 className="self-stretch justify-center text-stone-500 text-xl font-semibold font-['Rubik'] capitalize  text-center line-clamp-2">
                    {title}
                </h3>
            </div>
        </div>
    );
}

function FormacoesCursosCarouselSection({ cursos_session }: CursosProps) {
    const [api, setApi] = useState<CarouselApi>()
    const [current, setCurrent] = useState(0)

    useEffect(() => {
        if (!api) return;

        const onSelect = () => {
            setCurrent(api.selectedScrollSnap());
        };

        const onReInit = () => {
            setCurrent(api.selectedScrollSnap());
            try { api.scrollTo(0, true); } catch { }
        };

        onReInit();
        api.on("select", onSelect);
        api.on("reInit", onReInit);

        return () => {
            try {
                api.off?.("select", onSelect);
                api.off?.("reInit", onReInit);
            } catch { }
        };
    }, [api]);

    return (
        <div className="mb-12 lg:mb-16">
            {/* Layout responsivo: coluna em mobile, linha em desktop */}
            <div className="flex flex-col justify-center items-center lg:flex-row gap-8 lg:gap-12">
                {/* Título */}
                <div className="mb-4 lg:mb-0 lg:flex-1 lg:max-w-[33%] mt-10">
                    {cursos_session.title && (
                        <h3 className="text-zinc-800 text-2xl md:text-3xl font-['Rubik'] leading-9 mb-2 flex flex-wrap">
                            <HighlightText
                                text={cursos_session.title}
                                highlightWords={cursos_session.highlight_title || ''}
                                highlightClassName="font-bold"
                            />
                        </h3>
                    )}
                    {cursos_session.subtitle && (
                        <h4 className="text-cyan-700 text-xl md:text-2xl font-['Poppins'] leading-8 flex flex-wrap">
                            <HighlightText
                                text={cursos_session.subtitle}
                                highlightWords={cursos_session.highlight_subtitle || ''}
                                highlightClassName="font-bold"
                            />
                        </h4>
                    )}
                </div>

                {/* Carousel */}
                <div className="relative overflow-hidden lg:flex-1">
                    <Carousel
                        setApi={setApi}
                        opts={{ align: "start", loop: false }}
                        className="w-full"
                    >
                        <CarouselContent className="-ml-2 py-4">
                            {cursos_session.cursos && cursos_session.cursos.map((curso, index) => (
                                <CarouselItem
                                    key={`curso-${index}-${curso.title}`}
                                    className="pl-4 basis-full md:basis-1/2"
                                >
                                    <div className="h-full min-h-[380px] lg:min-h-[340px]">
                                        <CursoCard image={curso.image} title={curso.title} />
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    </Carousel>

                    {/* Indicadores de Paginação */}
                    {cursos_session.cursos && cursos_session.cursos.length > 1 && (
                        <div className="flex justify-center lg:justify-start gap-3 mt-4 lg:mt-0">
                            {Array.from({ length: cursos_session.cursos.length }).map((_, index) => (
                                <button
                                    key={`cursos-pagination-${index}`}
                                    onClick={() => api?.scrollTo(index)}
                                    className={`rounded-full transition-all cursor-pointer ${index === current
                                            ? 'w-12 h-3 bg-[#377396]'
                                            : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                                        }`}
                                    aria-label={`Ir para slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function FormacoesList({ formacoes }: FormacoesProps) {
    const [ativeTabe, setAtiveTab] = useState(0);


    return (<Tabs className={"w-full"} defaultValue="tab0">
        <TabsList className={"flex cursor-pointer rounded-4xl bg-gray-200 p-2 gap-2"} aria-label="Formacoes">
            {
                formacoes.map((formacao, index) =>
                    <TabsTrigger
                        key={index}
                        className={`rounded-4xl cursor-pointer border-0 p-2 bg-white w-full ${(ativeTabe == index) ? 'border-1 border-[red]' : 'border-0'}`}
                        value={`tab${index}`}
                        onClick={() => setAtiveTab(index)}
                    >
                        {formacao.page_type}
                    </TabsTrigger>
                )
            }
        </TabsList>
        {
            formacoes.map((formacao, index) =>
                <TabsContent key={index} className="TabsContent" value={`tab${index}`}>

                    {/** description */}
                    <div className="px-4 py-8 mb-6">
                        <RenderHtmlContentDescritpions htmlContent={formacao.description} className="font-poppins text-base lg:text-lg font-normal"></RenderHtmlContentDescritpions>
                    </div>

                    {/** cursos section carousel */}
                    {formacao.session_cursos && <FormacoesCursosCarouselSection cursos_session={formacao.session_cursos} />}

                    {/**session_cursos_e_objetivos */}
                    {
                        formacao.session_cursos_e_objetivos.map((objetivo, index) => (
                            <div key={index} className="px-4"><ItemDescSection itemData={objetivo} hasborder={true} /></div>
                        ))
                    }

                </TabsContent>
            )
        }
    </Tabs>);
};


export default function FormacoesPageClient({ formacoes }: SectionProps) {

    const formacoesData = [formacoes.session_inicial, formacoes.session_continua];

    return (
        <section className="py-16 md:py-24 bg-white lg:mb-24">
            <div className="container mx-auto px-4 md:px-8">

                {
                    formacoes && <FormacoesList formacoes={formacoesData} />
                }
            </div>
        </section>
    );
};
